import { useMemo, useReducer, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { Check, Plus, Search, X } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { localDayStartMs } from "../lib/dayHelpers";
import { formatNumber } from "../lib/format";
import { useLoggingFlow } from "../lib/logging-flow";
import { usePlate } from "../lib/plate";
import type { Screen } from "../components/nav/BottomNav";
import { HiddenIngredients } from "../components/meal-review/HiddenIngredients";
import { ReviewHeader } from "../components/meal-review/ReviewHeader";
import { ReviewItemCard } from "../components/meal-review/ReviewItemCard";
import { ReviewTotals } from "../components/meal-review/ReviewTotals";
import type {
  CustomFoodLike,
  MealReviewPayload,
  MemoryLike,
  ReviewSubmission,
} from "../components/meal-review/review-utils";
import {
  combineDateAndTime,
  hasMemorySourcedItem,
  hiddenIngredientsDefaultOpen,
  hiddenIngredientsVisible,
  initReviewState,
  itemCompleteness,
  reviewCompleteness,
  reviewFrictionForStyle,
  reviewItemFromCustomFood,
  reviewItemFromMemory,
  reviewReducer,
  reviewTotals,
  submitMealReview,
} from "../components/meal-review/review-utils";

// Unified Meal Review screen (plan todo 12) — the CENTREPIECE every capture
// flow (todos 13-18) and chat card (todo 23) lands on. Rendered full-screen
// by LoggingFlowHost for kind "review".
//
// PROPS CONTRACT (stable for todos 13-18, 23):
//   startFlow("review", payload) with payload: MealReviewPayload
//   (= LoggingFlowPayload + optional { uncertainty, date, mealType, name }).
//   - items: prefilled rows; unknown nutrition stays undefined (never 0).
//   - plateMode: true when the review came from the plate (cleared on log).
//   - source: capture source ("photo"|"text"|"voice"|"barcode"|...).
//   - imageStorageId: shows the photo thumbnail and attaches to the batch.
//   - uncertainty: { lowKcal, highKcal, reason } renders the range banner.
//   - date / mealType / name: optional preselects (defaults: today,
//     time-of-day guess, blank).
// Success: optimistic dashboard append -> clear plate (plateMode) -> closeFlow
// -> navigate to Today. Failure: inline error, review state fully preserved.

export interface MealReviewProps {
  readonly payload: MealReviewPayload;
  readonly demoMode?: boolean;
  readonly onNavigate: (screen: Screen) => void;
  readonly onClose: () => void;
}

type DashboardSummary = FunctionReturnType<typeof api.dashboard.getByDate>;

export function MealReview({ payload, demoMode = false, onNavigate, onClose }: MealReviewProps) {
  const [stableNow] = useState(() => Date.now());
  const [state, dispatch] = useReducer(reviewReducer, undefined, () =>
    initReviewState(payload, stableNow),
  );
  const [isLogging, setIsLogging] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [ingredientQuery, setIngredientQuery] = useState("");
  const { closeFlow } = useLoggingFlow();
  const { clear: clearPlate, addItem: addPlateItem } = usePlate();

  const imageStorageId = payload.imageStorageId as Id<"_storage"> | undefined;
  const [todayArgs] = useState(() => ({ now: stableNow, clientDayStart: localDayStartMs(stableNow) }));

  const profile = useQuery(api.profile.getProfile, demoMode ? "skip" : {});
  const today = useQuery(api.dashboard.getToday, demoMode ? "skip" : todayArgs);
  const imageUrl = useQuery(
    api.images.getUrl,
    !demoMode && imageStorageId !== undefined ? { storageId: imageStorageId } : "skip",
  );

  const trimmedIngredientQuery = ingredientQuery.trim();
  const searchArgs = { query: trimmedIngredientQuery, limit: 5 };
  const skipIngredientSearch = demoMode || !addPanelOpen || trimmedIngredientQuery.length === 0;
  const memoryResults = useQuery(api.foodMemory.search, skipIngredientSearch ? "skip" : searchArgs);
  const customFoodResults = useQuery(api.customFoods.search, skipIngredientSearch ? "skip" : searchArgs);

  const loggingStyle = typeof profile?.loggingStyle === "string" ? profile.loggingStyle : undefined;
  const friction = reviewFrictionForStyle(loggingStyle);
  const totals = useMemo(() => reviewTotals(state.items), [state.items]);
  const completeness = useMemo(() => reviewCompleteness(state.items), [state.items]);

  const goalKcal = typeof profile?.dailyGoalKcal === "number" ? profile.dailyGoalKcal : undefined;
  const consumedKcal = typeof today?.consumedKcal === "number" ? today.consumedKcal : undefined;
  const remainingKcal =
    goalKcal !== undefined && consumedKcal !== undefined ? goalKcal - consumedKcal : undefined;

  const logMeal = useMutation(api.meals.logMealBatch).withOptimisticUpdate((localStore, args) => {
    const knownKcal = args.items.reduce(
      (sum, item) => sum + (item.finalKcal ?? item.estimatedKcal ?? 0),
      0,
    );
    const unknownCount = args.items.filter((item) => item.kcalUnknown === true).length;
    const addKnownMetric = (current: number | null, added: number | undefined): number | null =>
      added === undefined ? current : (current ?? 0) + added;
    const sumMetric = (field: "proteinG" | "carbsG" | "fatG" | "fiberG"): number | undefined => {
      let total: number | undefined;
      for (const item of args.items) {
        const value = item[field];
        if (value !== undefined) total = (total ?? 0) + value;
      }
      return total;
    };
    const loggedAt = args.clientLoggedAt ?? Date.now();
    const tempEntries: Doc<"foodEntries">[] = args.items.map((item, index) => ({
      _id: `temp_${loggedAt}_${index}_${Math.random().toString(36).slice(2)}` as Id<"foodEntries">,
      _creationTime: loggedAt,
      userId: "optimistic" as Id<"users">,
      date: args.clientDayStart ?? args.date,
      name: item.name,
      ...(item.estimatedKcal === undefined ? {} : { estimatedKcal: item.estimatedKcal }),
      ...(item.finalKcal === undefined && item.estimatedKcal === undefined
        ? {}
        : { finalKcal: item.finalKcal ?? item.estimatedKcal }),
      ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
      ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
      ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
      ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
      mealType: args.mealType,
      ...(item.quantity === undefined ? {} : { quantity: item.quantity }),
      ...(item.unit === undefined ? {} : { unit: item.unit }),
      ...(item.confidence === undefined ? {} : { confidence: item.confidence }),
      ...(item.dataSource === undefined ? {} : { dataSource: item.dataSource }),
      ...(item.kcalUnknown === true ? { kcalUnknown: true } : {}),
      source: args.source,
      status: "logged",
      loggedAt,
    }));
    const applyToSummary = (current: DashboardSummary): DashboardSummary => ({
      ...current,
      consumedKcal: current.consumedKcal + knownKcal,
      netKcal: current.netKcal + knownKcal,
      remainingKcal: current.remainingKcal - knownKcal,
      consumedProteinG: addKnownMetric(current.consumedProteinG, sumMetric("proteinG")),
      consumedCarbsG: addKnownMetric(current.consumedCarbsG, sumMetric("carbsG")),
      consumedFatG: addKnownMetric(current.consumedFatG, sumMetric("fatG")),
      consumedFiberG: addKnownMetric(current.consumedFiberG, sumMetric("fiberG")),
      unknownKcalCount: current.unknownKcalCount + unknownCount,
      foodEntries: [...current.foodEntries, ...tempEntries],
    });
    // Today's screen subscription (Today.tsx) and the Chat sticky bar share
    // the same return shape; both get the optimistic append for this date
    // (only when the query is already loaded — Convex reconciles otherwise).
    const byDateArgs = { date: args.date, clientDayStart: args.clientDayStart };
    const byDate = localStore.getQuery(api.dashboard.getByDate, byDateArgs);
    if (byDate !== undefined) {
      localStore.setQuery(api.dashboard.getByDate, byDateArgs, applyToSummary(byDate));
    }
    const todayQueryArgs = { now: Date.now(), clientDayStart: args.clientDayStart };
    const todaySummary = localStore.getQuery(api.dashboard.getToday, todayQueryArgs);
    if (todaySummary !== undefined) {
      localStore.setQuery(api.dashboard.getToday, todayQueryArgs, applyToSummary(todaySummary));
    }
  });

  const handleLog = async () => {
    if (isLogging || confirmation !== null) return;
    if (state.items.length === 0) {
      setLogError("Add at least one item before logging this meal.");
      return;
    }
    if (state.items.some((item) => item.name.trim().length === 0)) {
      setLogError("Every item needs a name before logging.");
      return;
    }
    setLogError(null);
    setIsLogging(true);
    const submission: ReviewSubmission = {
      date: state.date,
      loggedAt: combineDateAndTime(state.date, state.time),
      mealType: state.mealType,
      ...(state.name.trim().length > 0 ? { name: state.name.trim() } : {}),
      ...(imageStorageId === undefined ? {} : { imageStorageId }),
      ...(payload.source === undefined ? {} : { source: payload.source }),
      saveAsMemory: state.saveAsMemory,
      items: state.items,
    };
    console.log("[Frontend][MealReview] Log meal requested", {
      date: submission.date,
      mealType: submission.mealType,
      itemCount: submission.items.length,
      plateMode: payload.plateMode === true,
      hasImage: imageStorageId !== undefined,
    });
    const result = await submitMealReview(logMeal, submission);
    if (result.ok) {
      if (payload.plateMode === true) {
        console.log("[Frontend][MealReview] Clearing plate after successful plate-mode log");
        clearPlate();
      }
      setConfirmation("Meal logged");
      window.setTimeout(() => {
        closeFlow();
        onNavigate("today");
      }, 700);
      return;
    }
    setIsLogging(false);
    // Failure path: inline error, review state preserved, NO navigation.
    setLogError(result.error);
  };

  const handleAddToPlate = () => {
    console.log("[Frontend][MealReview] Adding review items to plate", {
      itemCount: state.items.length,
    });
    for (const item of state.items) {
      addPlateItem({
        name: item.name,
        ...(item.quantity === undefined ? {} : { quantity: item.quantity }),
        ...(item.unit === undefined ? {} : { unit: item.unit }),
        ...(item.calories === undefined ? {} : { calories: item.calories }),
        ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
        ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
        ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
        ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
        dataSource: item.dataSource ?? "user-entered",
        ...(item.confidence === undefined ? {} : { confidence: item.confidence }),
      });
    }
    closeFlow();
  };

  const busy = isLogging || confirmation !== null;
  const uncertainty = payload.uncertainty;
  const incompleteItems = state.items.filter((item) => !itemCompleteness(item).complete).length;

  return (
    <div data-testid="meal-review" className="fixed inset-0 z-40 flex flex-col bg-muted">
      <header className="w-full shrink-0 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-lg font-semibold text-text">Review meal</h1>
            <p className="text-xs text-text-muted">
              {state.items.length} item{state.items.length === 1 ? "" : "s"}
              {incompleteItems > 0 ? ` · ${incompleteItems} incomplete` : ""}
            </p>
          </div>
          <button
            type="button"
            data-testid="review-close"
            aria-label="Close review"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-44">
        <div className="mx-auto flex max-w-xl flex-col gap-3 px-4 py-4">
          <ReviewHeader
            name={state.name}
            mealType={state.mealType}
            date={state.date}
            time={state.time}
            {...(payload.source === undefined ? {} : { source: payload.source })}
            imageUrl={imageStorageId === undefined ? null : imageUrl}
            onNameChange={(name) => dispatch({ type: "setName", name })}
            onMealTypeChange={(mealType) => dispatch({ type: "setMealType", mealType })}
            onStepDate={(days) => dispatch({ type: "stepDate", days })}
            onTimeChange={(time) => dispatch({ type: "setTime", time })}
          />

          {uncertainty !== undefined && (
            <div
              data-testid="review-uncertainty"
              className="rounded-2xl border border-warning/40 bg-amber-50 px-4 py-3"
            >
              <p className="text-sm font-semibold text-amber-800">
                Estimated {formatNumber(Math.round(uncertainty.lowKcal))}–
                {formatNumber(Math.round(uncertainty.highKcal))} kcal
              </p>
              <p className="mt-0.5 text-xs text-amber-700">{uncertainty.reason}</p>
            </div>
          )}

          <div className="flex flex-col gap-2" data-testid="review-items">
            {state.items.map((item, index) => (
              <ReviewItemCard
                key={item.key}
                item={item}
                index={index}
                showPortionWarning={friction.portionWarnings}
                showCompletenessBadge={friction.completenessBadge}
                collapseDetails={friction.collapseItemDetails}
                onPatch={(patch) => dispatch({ type: "updateItem", key: item.key, patch })}
                onRemove={() => dispatch({ type: "removeItem", key: item.key })}
              />
            ))}
            {state.items.length === 0 && (
              <p
                data-testid="review-items-empty"
                className="rounded-2xl border border-dashed border-border bg-surface px-4 py-6 text-center text-sm text-text-muted"
              >
                No items yet — add an ingredient below to start this meal.
              </p>
            )}
          </div>

          {state.removed !== null && (
            <div
              data-testid="review-undo"
              className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-text px-4 py-2"
            >
              <span className="truncate text-sm font-medium text-surface">
                Removed {state.removed.item.name}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  data-testid="review-undo-btn"
                  onClick={() => dispatch({ type: "undoRemove" })}
                  className="inline-flex min-h-[44px] items-center rounded-lg px-3 text-sm font-semibold text-accent-light transition-all duration-200 ease-out active:opacity-80 active:scale-[0.98] select-none"
                >
                  Undo
                </button>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => dispatch({ type: "dismissUndo" })}
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-surface/70 active:opacity-80 select-none"
                >
                  <X size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-border bg-surface p-3 shadow-sm">
            <button
              type="button"
              data-testid="review-add-ingredient"
              aria-expanded={addPanelOpen}
              onClick={() => setAddPanelOpen((open) => !open)}
              className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-border text-sm font-semibold text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
            >
              <Plus size={16} strokeWidth={2} aria-hidden="true" />
              Add ingredient
            </button>
            {addPanelOpen && (
              <div data-testid="review-add-panel" className="mt-3">
                <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2">
                  <Search size={16} strokeWidth={2} aria-hidden="true" className="text-text-muted" />
                  <input
                    type="text"
                    data-testid="review-ingredient-search"
                    value={ingredientQuery}
                    onChange={(event) => setIngredientQuery(event.target.value)}
                    placeholder="Search memories and custom foods"
                    aria-label="Search ingredients"
                    className="w-full bg-transparent text-base text-text placeholder:text-text-muted focus:outline-none"
                  />
                </div>
                {trimmedIngredientQuery.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1">
                    {(memoryResults === undefined || customFoodResults === undefined) && (
                      <div className="flex flex-col gap-1" data-testid="review-ingredient-loading">
                        {[0, 1].map((row) => (
                          <div key={row} className="h-9 animate-pulse rounded-lg bg-muted" />
                        ))}
                      </div>
                    )}
                    {memoryResults !== undefined &&
                      (memoryResults as MemoryLike[]).map((memory) => (
                        <button
                          key={`memory-${memory.foodName}`}
                          type="button"
                          data-testid="review-ingredient-result"
                          onClick={() => {
                            dispatch({ type: "addItem", item: reviewItemFromMemory(memory) });
                            setIngredientQuery("");
                          }}
                          className="flex min-h-[44px] items-center justify-between rounded-lg px-3 text-left text-sm text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
                        >
                          <span className="truncate font-medium">{memory.foodName}</span>
                          <span className="ml-2 shrink-0 text-xs text-text-muted">
                            {formatNumber(Math.round(memory.calories))} kcal · Memory
                          </span>
                        </button>
                      ))}
                    {customFoodResults !== undefined &&
                      (customFoodResults as CustomFoodLike[]).map((food) => (
                        <button
                          key={`custom-${food.name}`}
                          type="button"
                          data-testid="review-ingredient-result"
                          onClick={() => {
                            dispatch({ type: "addItem", item: reviewItemFromCustomFood(food) });
                            setIngredientQuery("");
                          }}
                          className="flex min-h-[44px] items-center justify-between rounded-lg px-3 text-left text-sm text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
                        >
                          <span className="truncate font-medium">{food.name}</span>
                          <span className="ml-2 shrink-0 text-xs text-text-muted">
                            {food.calories === undefined
                              ? "kcal unknown · Custom food"
                              : `${formatNumber(Math.round(food.calories))} kcal · Custom food`}
                          </span>
                        </button>
                      ))}
                    <button
                      type="button"
                      data-testid="review-ingredient-manual"
                      onClick={() => {
                        dispatch({
                          type: "addItem",
                          item: {
                            key: `review_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                            name: trimmedIngredientQuery,
                            dataSource: "user-entered",
                          },
                        });
                        setIngredientQuery("");
                      }}
                      className="flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-left text-sm font-semibold text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
                    >
                      <Plus size={14} strokeWidth={2} aria-hidden="true" />
                      Add “{trimmedIngredientQuery}” manually
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {hiddenIngredientsVisible(loggingStyle, {
            hasRange: uncertainty !== undefined,
            items: state.items,
          }) && (
            <HiddenIngredients
              defaultOpen={hiddenIngredientsDefaultOpen(payload.source, loggingStyle)}
              onAdd={(chipId) => dispatch({ type: "addHiddenIngredient", chipId })}
            />
          )}

          {friction.weighHint && hasMemorySourcedItem(state.items) && (
            <p
              data-testid="review-weigh-hint"
              className="rounded-2xl border border-border bg-surface px-4 py-3 text-xs font-medium text-text-muted"
            >
              This meal uses a food memory — weighing repeated meals now and then keeps your saved
              nutrition accurate.
            </p>
          )}

          <ReviewTotals
            totals={totals}
            {...(remainingKcal === undefined ? {} : { remainingKcal })}
            showCompleteness={friction.completenessBadge}
            completeItems={completeness.completeItems}
            totalItems={completeness.totalItems}
            percent={completeness.percent}
          />
        </div>
      </main>

      <footer className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex max-w-xl flex-col gap-2 px-4 pt-3 pb-3">
          {logError !== null && (
            <p
              data-testid="review-log-error"
              role="alert"
              className="rounded-xl bg-danger/10 px-3 py-2 text-sm font-medium text-danger"
            >
              {logError}
            </p>
          )}
          {confirmation !== null && (
            <p
              data-testid="review-confirmation"
              role="status"
              className="flex items-center gap-1.5 rounded-xl bg-accent-light px-3 py-2 text-sm font-medium text-accent-dark"
            >
              <Check size={15} strokeWidth={2.5} aria-hidden="true" />
              {confirmation}
            </p>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={state.saveAsMemory}
            data-testid="review-save-memory"
            onClick={() => dispatch({ type: "toggleSaveAsMemory" })}
            className="flex min-h-[44px] items-center justify-between rounded-xl px-2 text-left transition-all duration-200 ease-out active:bg-muted select-none"
          >
            <span>
              <span className="block text-sm font-semibold text-text">Save as food memory</span>
              <span className="block text-xs text-text-muted">
                Items with known calories are remembered for next time
              </span>
            </span>
            <span
              aria-hidden="true"
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                state.saveAsMemory ? "bg-accent" : "bg-border"
              }`}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${
                  state.saveAsMemory ? "left-[22px]" : "left-0.5"
                }`}
              />
            </span>
          </button>
          <div className={payload.plateMode === true ? "flex" : "grid grid-cols-2 gap-2"}>
            {payload.plateMode !== true && (
              <button
                type="button"
                data-testid="review-add-to-plate"
                onClick={handleAddToPlate}
                disabled={busy || state.items.length === 0}
                className="inline-flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl border border-border bg-surface px-3 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] disabled:opacity-50 select-none"
              >
                Add to plate
              </button>
            )}
            <button
              type="button"
              data-testid="review-log"
              onClick={() => void handleLog()}
              disabled={busy || demoMode || state.items.length === 0}
              className={`inline-flex w-full items-center justify-center gap-2 rounded-xl bg-accent font-semibold text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] disabled:opacity-50 select-none ${
                friction.prominentLogButton
                  ? "min-h-[56px] px-6 text-lg"
                  : "min-h-[48px] px-4 text-base"
              }`}
            >
              {isLogging ? "Logging…" : "Log meal"}
            </button>
          </div>
          {demoMode && (
            <p data-testid="review-demo-note" className="text-center text-xs text-text-muted">
              Demo mode — logging needs a signed-in account.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}

export default MealReview;
